import { useCallback, useMemo } from 'react';
import { hasMenuAccess, hasFeatureAccess } from '../../../utils/menuAccess';
import { useGetMarketplacePlans } from '../marketplace/marketplace.queries';

/**
 * Hook para gerenciar controle de acesso de menus e funcionalidades
 */
export const useMenuAccess = () => {
  // Busca os planos do marketplace
  const { data: marketplacePlans = [], isLoading } = useGetMarketplacePlans();

  /**
   * Verifica se tem acesso a um menu específico
   */
  const checkMenuAccess = useCallback((parentIndex, childIndex = null, userData = null) => {
    return hasMenuAccess(parentIndex, childIndex, marketplacePlans, userData);
  }, [marketplacePlans]);

  /**
   * Verifica se tem acesso a uma funcionalidade específica
   */
  const checkFeatureAccess = useCallback((feature, userData = null) => {
    return hasFeatureAccess(feature, marketplacePlans, userData);
  }, [marketplacePlans]);

  /**
   * Verifica se é um usuário com acesso total (plano com categoria "ALL")
   */
  const isFullAccessUser = useMemo(() => {
    const activePlan = marketplacePlans.find(plan => plan.status === 1);
    return activePlan?.categories?.includes('ALL') || false;
  }, [marketplacePlans]);

  /**
   * Retorna uma função que pode ser usada como guard em componentes
   */
  const createAccessGuard = useCallback((feature) => {
    return (component) => {
      if (!checkFeatureAccess(feature)) {
        return null; // ou componente de acesso negado
      }
      return component;
    };
  }, [checkFeatureAccess]);

  return {
    marketplacePlans,
    isLoading,
    checkMenuAccess,
    checkFeatureAccess,
    isFullAccessUser,
    createAccessGuard,
    
    // Shortcuts para funcionalidades específicas
    canAccessPurchaseOrders: checkFeatureAccess('purchase-orders'),
    canAccessStockAnalysis: checkFeatureAccess('stock-analysis'),
  };
};